import { ChangeDetectorRef, Component, inject, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { Call, CallAccessLevel, CallShare, CallsService } from '../../calls.service'
import { MAT_DIALOG_DATA, MatDialog, MatDialogClose, MatDialogTitle } from '@angular/material/dialog'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatCheckbox } from '@angular/material/checkbox'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { MatDivider } from '@angular/material/divider'
import { FormsModule } from '@angular/forms'
import { MatAutocompleteModule, MatOption } from '@angular/material/autocomplete'
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { filter, firstValueFrom, map } from 'rxjs'
import { ToastService } from '../../toast.service'
import { User, UsersService } from '../../users.service'
import { MatIcon } from '@angular/material/icon'
import { MatTooltip } from '@angular/material/tooltip'
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component'
import { constructNameForUser } from '../../utils'
import { SpinnerComponent } from '../../spinner/spinner.component'
import { UserNamePipe } from '../../user-name.pipe'
import { AsyncPipe } from '@angular/common'
import { AvatarComponent } from '../../avatar/avatar.component'

@Component({
  selector: 'app-share-call-dialog',
  standalone: true,
  imports: [
    MatButton,
    MatIconButton,
    MatCheckbox,
    TranslatePipe,
    TranslateDirective,
    MatDialogClose,
    MatDialogTitle,
    MatDivider,
    FormsModule,
    MatOption,
    MatAutocompleteModule,
    MatMenu,
    MatMenuContent,
    MatMenuItem,
    MatMenuTrigger,
    MatIcon,
    MatTooltip,
    SpinnerComponent,
    AvatarComponent,
    UserNamePipe,
    AsyncPipe,
  ],
  templateUrl: './share-call-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './share-call-dialog.component.scss',
})
export class ShareCallDialogComponent implements OnInit {
  callsService = inject(CallsService)
  toastService = inject(ToastService)
  usersService = inject(UsersService)
  dialog = inject(MatDialog)
  translateService = inject(TranslateService)
  changeDetectorRef = inject(ChangeDetectorRef)

  updateShareLevelMutation = this.callsService.updateCallAccessLevel(this.call.id)
  shareToUserMutation = this.callsService.shareToUser(this.call.id)
  removeShareMutation = this.callsService.removeShareMutation(this.call.id)

  isSharePending$ = this.shareToUserMutation.result$.pipe(map((result) => result.isPending))

  readonly levels: CallAccessLevel[] = ['COMMENTER', 'REVIEWER', 'EDITOR']

  canEdit = false
  currentUserId = this.usersService.currentUser$.value?.id ?? null
  userToShareTo: User | null = null
  email = ''
  level: CallAccessLevel = 'COMMENTER'
  notifyByEmail = true
  filteredOrganizationUsers: User[] = []
  organizationUsers: User[] = []

  constructor(@Inject(MAT_DIALOG_DATA) public call: Call) {}

  ngOnInit() {
    this.initializeOrganizationUsers()
    this.canEdit = this.call.accessLevel === 'FULL_ACCESS'
  }

  async updateShareLevel(share: CallShare, level: CallAccessLevel) {
    try {
      await this.updateShareLevelMutation.mutateAsync({ sharedUserId: share.sharedUserId, level })
      share.level = level
      this.changeDetectorRef.detectChanges()
      this.toastService.show('CALLS.SHARE_DIALOG.UPDATED_NOTIFICATION')
    } catch (error) {
      this.toastService.show('CALLS.SHARE_DIALOG.UPDATE_ERROR')
    }
  }

  filterOrganizationUsers(input: string) {
    this.filteredOrganizationUsers = this.organizationUsers
      .filter((user) => !this.call.users.map((callUser) => callUser.id).includes(user.id))
      .filter((user) => !this.call.shares.map((share) => share.sharedUserId).includes(user.id))
      .filter((user) => user.id !== this.usersService.currentUser$.value?.id)
      .filter((user) => (constructNameForUser(user) + user.email).toLowerCase().includes(input))
  }

  displayUserOnAutocomplete(user: User): string {
    return user !== null && typeof user === 'object' ? constructNameForUser(user) : ''
  }

  async shareCall(): Promise<void> {
    const userToShareTo = this.resolveShareTarget()
    if (userToShareTo === null) {
      return
    }
    try {
      const share = await this.shareToUserMutation.mutateAsync({
        sharedUserId: userToShareTo.id,
        level: this.level,
        notifyByEmail: this.notifyByEmail,
      })
      this.call.shares.push(share)
      this.resetShareForm()
      this.toastService.show('CALLS.SHARE_DIALOG.SHARED_NOTIFICATION')
    } catch (error) {
      this.toastService.show('CALLS.SHARE_DIALOG.SHARE_ERROR')
    }
  }

  async removeShare(sharedUser: User) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('CALLS.SHARE_DIALOG.REMOVE_SHARE.TITLE'),
        description: this.translateService.instant('CALLS.SHARE_DIALOG.REMOVE_SHARE.DESCRIPTION', { userName: constructNameForUser(sharedUser) }),
      },
    })

    const result = await firstValueFrom(dialogRef.afterClosed())
    if (result) {
      await this.removeShareMutation.mutateAsync(sharedUser.id)
      this.call.shares = this.call.shares.filter((share) => share.sharedUserId !== sharedUser.id)
      this.changeDetectorRef.detectChanges()
      this.toastService.show('CALLS.SHARE_DIALOG.SHARE_REMOVED_NOTIFICATION')
    }
  }

  isValidInput() {
    const userToShareTo = this.resolveShareTarget()
    if (userToShareTo === null) {
      return false
    }

    const isAlreadyShared = this.call.shares.some((share) => share.sharedUserId === userToShareTo.id)
    const isCallUser = this.call.users.some((user) => user.id === userToShareTo.id)
    return !isAlreadyShared && !isCallUser && userToShareTo.id !== this.currentUserId
  }

  private resolveShareTarget(): User | null {
    return this.userToShareTo ?? this.findOrganizationUserByEmail()
  }

  private findOrganizationUserByEmail(): User | null {
    const email = this.email.trim().toLowerCase()
    return this.organizationUsers.find((user) => user.email.toLowerCase() === email) ?? null
  }

  private resetShareForm() {
    this.userToShareTo = null
    this.email = ''
    this.level = 'COMMENTER'
  }

  private initializeOrganizationUsers() {
    this.usersService
      .getOrganizationUsers()
      .pipe(
        filter((result) => result.isSuccess),
        map((result) => result.data)
      )
      .subscribe((users) => (this.organizationUsers = users!))
  }
}
